import React, { useState, useEffect } from 'react';
import { Plus, Home, Trash2 } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import DataGrid from '../../components/DataGrid';
import { useAuth } from '../../context/AuthContext';
import { unitTypes as api, type UnitType, type ApiError } from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};
const actionBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '4px 6px', borderRadius: 6, color: 'var(--nx-text-muted)',
  display: 'inline-flex', alignItems: 'center',
};

const INIT = { name: '' };

const UnitTypesPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems]       = useState<UnitType[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm]         = useState(INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmItem, setConfirmItem] = useState<UnitType | null>(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const openAdd = () => { setFormError(''); setForm(INIT); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(INIT); setFormError(''); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    try {
      const created = await api.create(societyId, { name: form.name });
      setItems(p => [created, ...p]);
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to save flat type. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: UnitType) => { setConfirmItem(item); setDeleteError(''); };

  const confirmDelete = async () => {
    if (!societyId || !confirmItem) return;
    setDeletingId(confirmItem.id);
    try {
      await api.delete(societyId, confirmItem.id);
      setItems(p => p.filter(x => x.id !== confirmItem.id));
      setConfirmItem(null);
    } catch { setDeleteError('Failed to delete flat type. Please try again.'); }
    finally { setDeletingId(null); }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Flat Types
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            {items.length} type{items.length !== 1 ? 's' : ''} configured — shown when adding or editing a unit
          </p>
        </div>
        <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Flat Type
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            {
              key: 'name', header: 'Flat Type',
              cellStyle: { fontWeight: 500 },
              render: (i: UnitType) => i.name,
            },
            {
              key: 'actions', header: '',
              cellStyle: { textAlign: 'right', whiteSpace: 'nowrap' },
              render: (i: UnitType) => (
                <button
                  onClick={() => handleDelete(i)}
                  disabled={deletingId === i.id}
                  style={{ ...actionBtn, color: '#EF4444', opacity: deletingId === i.id ? 0.4 : 1 }}
                  title="Delete flat type"
                >
                  <Trash2 size={14} />
                </button>
              ),
            },
          ]}
          rows={items}
          rowKey={i => i.id}
          loading={loading}
          emptyIcon={<Home size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No flat types yet. Add 1BHK, 2BHK, 3BHK, etc."
        />
      </div>

      <ConfirmDialog
        open={confirmItem !== null}
        variant="danger"
        title={`Delete "${confirmItem?.name}"?`}
        message="Units using this flat type will lose that association. This action cannot be undone."
        confirmLabel="Delete Flat Type"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmItem(null); setDeleteError(''); }}
      />

      {showForm && (
        <Modal
          title="Add Flat Type"
          submitLabel="Add Flat Type"
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Flat Type Name *</label>
            <input
              name="name"
              value={form.name}
              onChange={e => setForm({ name: e.target.value })}
              required
              placeholder="e.g. 2BHK"
              style={inp}
              autoFocus
            />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default UnitTypesPage;
